// BUG-11-CHG-01: stays on raw useAccount + useSignTypedData by design. Login UI hidden when isLoggedIn === true; impersonation requires isLoggedIn === true → login flow cannot trigger during impersonation. See arandao-docs/architecture/impersonation-ux.md §Integration Points exempt list.
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import {
  useAccount,
  useChainId,
  useSignTypedData,
  useSwitchChain,
} from "wagmi";
import { EXPECTED_CHAIN_ID } from "@/configs/expected-chain";
import { useAuth } from "@/context/auth";
import { useContractErrorHandler } from "@/hooks/useContractErrorHandler";
import { useRouter } from "@/i18n/navigation";
import { $api } from "@/lib/api-client";
import {
  type AuthResponse,
  completePasswordSecondFactor,
  isPasswordSecondFactorRequired,
  type PasswordSecondFactorRequired,
} from "@/lib/auth-password-second-factor";
import { isMetaMaskMobileInAppBrowser } from "@/lib/wallet/detect-mm-mobile";
import { MetaMaskMobileBridgeError } from "@/lib/wallet/errors";

// REMOVE WHEN https://github.com/MetaMask/metamask-mobile/issues/29639 closes.
// (BUG-27 mitigation — `isMetaMaskBridgeError` helper + the inner try/catch
// around `signTypedDataAsync` below + the `MetaMaskMobileBridgeError` /
// detector imports above all delete in one revert PR when MM ships upstream
// fix. Sentry lazy-import pattern: src/lib/transaction-logger.ts:188-222
// (production-only lazy import + try/catch wrapper). `.cause` walk pattern:
// src/hooks/useContractError.ts:138 (canonical `error.cause instanceof X`
// idiom). FORWARD-COMPAT NOTE: src/lib/telegram.ts is a no-op stub since
// 2026-04-29 so the Sentry capture below does NOT generate Telegram noise
// today; if Telegram is ever re-enabled, instrumentation-client.ts
// `beforeSend` would forward this event to Telegram, duplicating the signal
// — that future restore work-unit must add a beforeSend filter for
// `event.tags?.metamask_mobile_bridge_failure === "true"`.

/**
 * Returns true iff `err` (or any error in its `.cause` chain) is a TypeError
 * whose message matches the MM Mobile bridge crash signature
 * `connector.getChild is not a function`. wagmi/viem may wrap the original
 * TypeError; we walk the chain so the typed-error branch fires regardless.
 *
 * depth=8 covers viem 2.x wrap patterns (≤3 in practice); revisit if future
 * viem releases wrap deeper. Bound also serves as cycle guard for pathological
 * `.cause` self-references.
 */
function isMetaMaskBridgeError(err: unknown): boolean {
  let cur: unknown = err;
  for (let depth = 0; depth < 8 && cur instanceof Error; depth += 1) {
    if (
      cur instanceof TypeError &&
      /connector\.getChild is not a function/.test(cur.message)
    ) {
      return true;
    }
    cur = cur.cause;
  }
  return false;
}

function isAuthResponse(value: unknown): value is AuthResponse {
  return (
    !!value &&
    typeof value === "object" &&
    "access_token" in value &&
    typeof value.access_token === "string" &&
    "user" in value &&
    !!value.user &&
    typeof value.user === "object" &&
    "id" in value.user &&
    typeof value.user.id === "string" &&
    "wallet_address" in value.user &&
    typeof value.user.wallet_address === "string"
  );
}

export default function useLogin(onSuccess?: () => void) {
  const [secondFactorChallenge, setSecondFactorChallenge] =
    useState<PasswordSecondFactorRequired | null>(null);
  const { address } = useAccount();
  const chainId = useChainId();
  const { switchChainAsync } = useSwitchChain();
  const { signTypedDataAsync } = useSignTypedData();
  const { handleContractError } = useContractErrorHandler();

  const auth = useAuth();
  const router = useRouter();
  const loginMutation = $api.useMutation("post", "/auth/login");

  const finishLogin = (authData: AuthResponse | null | undefined) => {
    if (!authData?.access_token || !authData.user) return;

    auth.setIsLoggedIn(true);
    auth.setToken(authData.access_token);
    auth.setUser(authData.user);
    setSecondFactorChallenge(null);

    setTimeout(() => {
      auth.setIsLoginDialogOpen(false);
      router.refresh();
    }, 0);

    onSuccess?.();
  };

  const completeSecondFactorMutation = useMutation({
    mutationKey: ["login", "password-second-factor"],
    mutationFn: (value: { password: string }) => {
      if (!secondFactorChallenge) {
        throw new Error("Second factor challenge is not available");
      }

      return completePasswordSecondFactor({
        challenge_token: secondFactorChallenge.challenge_token,
        password: value.password,
      });
    },
    onSuccess: (data) => {
      finishLogin(data);
    },
    // No onError: a wrong/expired second-factor password is an expected user
    // outcome, mapped to the form's password field by PasswordSecondFactorForm.
    // Routing it through handleContractError would toast the raw, untranslated
    // backend code (and, if Telegram reporting is ever restored, spam it).
  });

  const login = async (values: {
    wallet_address: `0x${string}`;
    nickname: string;
  }) => {
    if (!chainId) {
      throw new Error(
        "Wallet chain ID is not available. Please connect your wallet.",
      );
    }

    // Convert chainId to number if it's a string
    const chainIdNumber =
      typeof chainId === "string" ? Number.parseInt(chainId, 10) : chainId;

    // EXPECTED_CHAIN_ID resolves to the fork chainId when NEXT_PUBLIC_RPC_URL
    // is set, otherwise Polygon mainnet (or Amoy testnet) per CONTRACT_NET.
    const expectedChainId = EXPECTED_CHAIN_ID;

    // Enforce that login must be on the expected chain
    // Switch to the correct chain if not already on it
    if (chainIdNumber !== expectedChainId) {
      try {
        await switchChainAsync({ chainId: expectedChainId });
      } catch (error) {
        // Handle contract/wallet errors (chain switch rejection, etc.)
        handleContractError(error);
        throw error; // Re-throw to prevent continuing with wrong chain
      }
    }

    try {
      const typeData = {
        types: {
          Arandao: [
            { name: "wallet", type: "string" },
            { name: "nickname", type: "string" },
          ],
        },
        primaryType: "Arandao" as const,
        domain: {
          name: "AranDao.xyz",
          version: "1",
          chainId: expectedChainId, // Always use the expected chain ID (Polygon or Polygon Amoy)
        },
        message: {
          wallet: address,
          nickname: values?.nickname,
        },
      };

      let signature: `0x${string}`;
      try {
        signature = await signTypedDataAsync(typeData);
      } catch (signError) {
        if (isMetaMaskBridgeError(signError)) {
          // Capture to Sentry with PM-locked tag (fail-soft on Sentry import failure).
          if (typeof window !== "undefined") {
            try {
              const Sentry = await import("@sentry/nextjs");
              Sentry.withScope((scope) => {
                scope.setTag("metamask_mobile_bridge_failure", "true");
                scope.setTag(
                  "is_mm_mobile_inapp",
                  String(isMetaMaskMobileInAppBrowser()),
                );
                scope.setExtra("user_agent", navigator.userAgent);
                Sentry.captureException(signError);
              });
            } catch {
              // Sentry not loaded (dev / SSR / blocked / instrumentation
              // failure). Swallow — primary behaviour is throwing the typed
              // error so the toast still fires downstream.
            }
          }
          throw new MetaMaskMobileBridgeError(signError);
        }
        // Any other sign-error (including a TypeError with a different
        // missing-method prefix, e.g. "foo.getChild is not a function"):
        // existing outer-catch handles via handleContractError.
        throw signError;
      }

      const loginBody = {
        wallet_address: values.wallet_address,
        signature,
        nickname: values?.nickname,
        message: typeData as unknown as Record<string, never>,
      };

      const response = await loginMutation.mutateAsync({
        body: loginBody,
      });

      if (isPasswordSecondFactorRequired(response.data)) {
        setSecondFactorChallenge(response.data);
      }

      return response;
    } catch (error) {
      // Handle all errors (contract/wallet errors from signTypedDataAsync or API errors from loginMutation)
      // handleContractError will show appropriate Persian message for recognized errors
      // including "Account not active" and other contract errors
      handleContractError(error);

      // Re-throw to let React Query handle it
      throw error;
    }
  };

  const mutation = useMutation({
    mutationKey: ["login"],
    mutationFn: (value: {
      wallet_address: `0x${string}`;
      nickname: string;
    }) => {
      return login(value);
    },

    onSuccess: (data) => {
      const payload = data?.data;

      // Second factor required: the challenge was already stored in login().
      if (isPasswordSecondFactorRequired(payload)) return;

      // Normal login: commit auth state.
      if (isAuthResponse(payload)) {
        finishLogin(payload);
        return;
      }

      // A 200 with a shape we don't recognize (neither auth data nor a known
      // 2FA challenge). Surface it through the standard login-error path instead
      // of silently stalling the dialog with the spinner stopped.
      handleContractError(new Error("unexpected_login_response"));
    },
  });

  return {
    ...mutation,
    secondFactorChallenge,
    clearSecondFactorChallenge: () => setSecondFactorChallenge(null),
    completeSecondFactor: completeSecondFactorMutation,
  };
}
